import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import DataSync from "../Webflow/DataSync";
import Appearance from "../Webflow/Appearence";
import DefaultQuestions from "../Webflow/DefaultQuestions";
import RecaptchaSettings from "../Webflow/Recaptcha";
import EmailTemplate from "../Webflow/EmailTemplate";
import Users from "../Webflow/Users";
import Script from "../Webflow/Script";
import ExternalMedia from "../Webflow/ExternalVideos";
import Integrations from "../Webflow/Integrations";
import { useDomain } from "context/DomainContext";

import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Drawer,
  Typography,
  CircularProgress,
} from "@mui/material";

import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import GroupIcon from "@mui/icons-material/Group";
import ExtensionIcon from "@mui/icons-material/Extension";
import CodeIcon from "@mui/icons-material/Code";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import { useSoftUIController } from "context";
import { setMiniSidenav } from "context";
import UserPricing from "../Webflow/UserPricing";
import { Payment } from "@mui/icons-material";
import { useAuth } from "context/AuthContext";

export default function Settings() {
  const { source } = useDomain();
  const [tabIndex, setTabIndex] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav } = controller;
  const { user, isAuthenticated, getCurrentRole, loading, isInitialized } = useAuth();
  const role = getCurrentRole() || sessionStorage.getItem("role");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  useEffect(() => {
    setMiniSidenav(dispatch, !miniSidenav);
  }, []);

  useEffect(() => {
    if (isMobile) setCollapsed(false);
    else if (isTablet) setCollapsed(true);
  }, [isMobile, isTablet]);

  const filteredTabs = useMemo(() => {
    if (!source) return []; // Return empty array if source is not available

    let tabs = [
      { label: "Scripts", component: <Script />, icon: <CodeIcon fontSize="small" /> },
      { label: "Appearance", component: <Appearance />, icon: <ColorLensIcon fontSize="small" /> },
      { label: "Email template", component: <EmailTemplate />, icon: <EmailIcon fontSize="small" /> },
      { label: "Google Recaptcha", component: <RecaptchaSettings />, icon: <SecurityIcon fontSize="small" /> },
      { label: "Data Sync", component: <DataSync />, icon: <SyncIcon fontSize="small" /> },
      { label: "Default Questions", component: <DefaultQuestions />, icon: <HelpOutlineIcon fontSize="small" /> },
      { label: "External Media", component: <ExternalMedia />, icon: <VideoLibraryIcon fontSize="small" /> },
      { label: "Users", component: <Users />, icon: <GroupIcon fontSize="small" /> },
      // { label: "Payments", component: <UserPricing />, icon: <Payment fontSize="small" /> },
    ];

    if (source === "wordpress") {
      tabs = tabs.filter((tab) => tab.label !== "Data Sync");
    }
    if (source === "webflow" || source === "wordpress" || source === "") {
      tabs = tabs.filter((tab) => tab.label !== "Scripts");
    }
    if(role==="read&write"){
      tabs = tabs.filter((tab) => tab.label !== "Integrations");
    }
  if (role!=="admin") {
      tabs = tabs.filter((tab) => tab.label !== "Users");
    }

    return tabs;
  }, [source, role]);

  useEffect(() => {
    if (!source) return; // Don't set tab index if source is not available

    const savedTab = sessionStorage.getItem("settingsTabIndex");
    if (savedTab !== null && filteredTabs[savedTab]) {
      setTabIndex(Number(savedTab));
    } else {
      if (source === "custom" || source === "wix" || source === "squarespace") {
        const scriptsIndex = filteredTabs.findIndex((tab) => tab.label === "Scripts");
        if (scriptsIndex !== -1) setTabIndex(scriptsIndex);
        else setTabIndex(0);
      } else {
        setTabIndex(0);
      }
    }
  }, [source, filteredTabs]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleTabSelect = (index) => {
    setTabIndex(index);
    sessionStorage.setItem("settingsTabIndex", index); 
    if (isMobile) setMobileOpen(false);
  };

  const SidebarContent = ({ isMobileDrawer = false }) => (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        width: "100%",
      }}
    >
      {isMobileDrawer && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Settings
          </Typography>
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {!isMobileDrawer && !isMobile && (
        <Box display="flex" justifyContent={collapsed ? "center" : "flex-end"} width="100%" p={1}>
          <IconButton onClick={() => setCollapsed(!collapsed)} size="small">
            {collapsed ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      <List sx={{ flexGrow: 1, width: "100%", px: isMobileDrawer ? 1 : 2 }}>
        {filteredTabs.map((tab, index) => (
          <Tooltip
            title={!isMobileDrawer && collapsed ? tab.label : ""}
            placement="right"
            key={tab.label}
          >
            <ListItemButton
              selected={tabIndex === index}
              onClick={() => handleTabSelect(index)}
              sx={{
                borderRadius: "8px",
                margin: "4px",
                paddingY: 1,
                paddingX: !isMobileDrawer && collapsed ? 1 : 2,
                "&.Mui-selected": {
                  backgroundColor: "#219297",
                  color: "#fff",
                  "& .MuiListItemIcon-root": { color: "#fff" },
                  "&:hover": {
                    backgroundColor: "#39b1b8",
                    color: "#fff",
                    "& .MuiListItemIcon-root": { color: "#fff" },
                  },
                },
                "&:hover": {
                  backgroundColor: "#39b1b8",
                  color: "#fff",
                  "& .MuiListItemIcon-root": { color: "#fff" },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: !isMobileDrawer && collapsed ? "auto" : 38 }}>
                {tab.icon}
              </ListItemIcon>
              {(isMobileDrawer || !collapsed) && (
                <ListItemText
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontSize: isMobileDrawer ? "0.9rem" : "0.8rem",
                    fontWeight: 500,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Box>
  );

  // Show loading spinner until source is available
  if (!source) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          sx={{ minHeight: "70vh" }}
        >
          <CircularProgress 
            size={60} 
            sx={{ 
              color: "#219297",
              animationDuration: "800ms" 
            }} 
          />
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isMobile && (
        <SoftBox mb={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              padding: 2,
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
              {filteredTabs[tabIndex]?.label || "Settings"}
            </Typography>
            <IconButton onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
          </Box>
        </SoftBox>
      )}

      <SoftBox display="flex" mt={isMobile ? 1 : 2} sx={{ minHeight: "70vh" }}>
        {!isMobile && (
          <Box
            sx={{
              position: "sticky",
              top: 90,
              height: "auto",
              width: collapsed ? 75 : { md: 200, lg: 220 },
              minWidth: collapsed ? 75 : { md: 200, lg: 220 },
              transition: "all 0.3s ease",
              borderRight: "1px solid #e0e0e0",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: collapsed ? "center" : "flex-start",
              mr: 2,
            }}
          >
            <SidebarContent />
          </Box>
        )}

        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{ "& .MuiDrawer-paper": { boxSizing: "border-box", width: 280 } }}
          >
            <SidebarContent isMobileDrawer={true} />
          </Drawer>
        )}

        <SoftBox
          flexGrow={1}
          sx={{ width: isMobile ? "100%" : "auto", overflowY: "auto", maxHeight: "calc(100vh - 120px)" }}
        >
          <Box
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              minHeight: "20vh",
              border: "1px solid #e0e0e0",
              p: 0,
            }}
          >
            {filteredTabs[tabIndex]?.component}
          </Box>
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
}